import { Decimal, decimal } from "../money.js";
import { STRATEGY } from "./strategy.js";
function abs(value) {
    return value.units < 0n ? new Decimal(-value.units, value.scale) : value;
}
const atLeast = (value, threshold) => value.compare(decimal(threshold)) >= 0;
/** `skew_offsets(width, skew, share_percent)`, exact. */
export function skewOffsets(width, skew) {
    const w = decimal(width);
    const share = decimal(STRATEGY.gate.skewSharePercent).div(decimal("100"), 2);
    const rest = decimal("1").sub(share);
    const pick = skew === "up"
        ? { below: w.mul(rest), above: w.mul(share) }
        : skew === "down"
            ? { below: w.mul(share), above: w.mul(rest) }
            : { below: w.div(decimal("2"), 2), above: w.div(decimal("2"), 2) };
    return {
        below: new Decimal(-pick.below.units, pick.below.scale).toString(),
        above: pick.above.toString(),
    };
}
/** The first re-entry condition that still holds the block, or null when all are released. */
function reentryHold(metrics, elapsedMs) {
    const { reentryMinSeconds, reentryTrend, reentryDistance, volSkipRatio } = STRATEGY.gate;
    if (!(elapsedMs >= reentryMinSeconds * 1000))
        return {
            metric: "elapsed_seconds",
            value: String(Math.floor(elapsedMs / 1000)),
            threshold: String(reentryMinSeconds),
        };
    if (atLeast(abs(decimal(metrics.trend)), reentryTrend))
        return { metric: "trend", value: metrics.trend, threshold: reentryTrend };
    if (atLeast(abs(decimal(metrics.distance)), reentryDistance))
        return { metric: "distance", value: metrics.distance, threshold: reentryDistance };
    if (atLeast(decimal(metrics.volRatio), volSkipRatio))
        return { metric: "vol_ratio", value: metrics.volRatio, threshold: volSkipRatio };
    return null;
}
/** `entry_gate.evaluate` in `full` mode, with the re-entry block after a stop close. */
export function evaluateGate(measurement, context) {
    const g = STRATEGY.gate;
    const blockedAt = context.blockedAtMs ?? null;
    if (measurement.insufficient)
        return { enter: false, reason: "insufficient_history", reentryCleared: false };
    let reentryCleared = false;
    if (blockedAt !== null) {
        if (!Number.isFinite(blockedAt) || !Number.isFinite(context.nowMs))
            throw new RangeError("Invalid time");
        const hold = reentryHold(measurement, context.nowMs - blockedAt);
        if (hold)
            return { enter: false, reason: "reentry_blocked", ...hold, reentryCleared: false };
        reentryCleared = true;
    }
    const volRatio = decimal(measurement.volRatio);
    const trend = decimal(measurement.trend);
    const distance = decimal(measurement.distance);
    const skip = (reason, metric, value, threshold) => ({ enter: false, reason, metric, value, threshold, reentryCleared });
    if (atLeast(volRatio, g.volSkipRatio))
        return skip("high_volatility", "vol_ratio", measurement.volRatio, g.volSkipRatio);
    if (atLeast(abs(distance), g.distanceSkip))
        return skip("far_from_mean", "distance", measurement.distance, g.distanceSkip);
    if (atLeast(abs(trend), g.trendSkip))
        return skip("strong_trend", "trend", measurement.trend, g.trendSkip);
    const elevated = atLeast(volRatio, g.volElevatedRatio) || atLeast(abs(distance), g.distanceCaution);
    const width = elevated ? g.elevatedWidthPercent : g.normalWidthPercent;
    const skew = !atLeast(abs(trend), g.trendSkew)
        ? "centered"
        : trend.units > 0n
            ? "up"
            : trend.units < 0n
                ? "down"
                : "centered";
    return { enter: true, width, skew, ...skewOffsets(width, skew), reentryCleared };
}
